import React, { useState, useEffect } from 'react';
import { crApi, Employee, CRDetail } from '../services/crApi';
import { projectApi, Project as BackendProject } from '../services/projectApi';
import NewProjectModal from '../components/NewProjectModal';
import { useAlert } from '../contexts/AlertContext';

interface CRDetailPanelProps {
  isOpen: boolean;
  employee: Employee | null;
  crDetails: CRDetail[];
  selectedYear: number;
  onClose: () => void;
  onProjectClick: (projectId: number, projectName: string) => void;
}

const CRDetailPanel: React.FC<CRDetailPanelProps> = ({ isOpen, employee, crDetails, selectedYear, onClose, onProjectClick }) => {
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('ko-KR').format(Math.round(num));
  };

  const formatMM = (num: number) => {
    return Number(num || 0).toFixed(1);
  };

  // 합계 계산
  const totalBilling = crDetails.reduce((sum, d) => sum + d.projectRevenue, 0);
  const totalMM = crDetails.reduce((sum, d) => sum + d.yearlyMM, 0);
  // 연간 인력원가: 첫 번째 항목의 inputCost (모든 프로젝트 동일)
  const annualLaborCost = crDetails.length > 0 ? crDetails[0].inputCost : 0;
  // CR = 총 빌링액 / 연간 인력원가 × 100
  const coveredRevenuePct = annualLaborCost > 0 ? (totalBilling / annualLaborCost) * 100 : 0;
  // MM% = 총 투입MM / 12 × 100
  const mmPct = (totalMM / 12) * 100;

  if (!isOpen || !employee) return null;

  return (
    <div className="fixed inset-0 z-30">
      <div className="absolute inset-0 bg-black bg-opacity-20" onClick={onClose}></div>
      <div
        className={`absolute top-0 right-0 h-full w-full max-w-5xl bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <header className="h-16 flex-shrink-0 border-b border-slate-200 flex items-center justify-between px-6">
          <div>
            <h2 className="text-lg font-bold">{employee.name} CR상세 내역</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full text-slate-500 hover:bg-slate-100 text-2xl"
          >
            ×
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="bg-white rounded-xl border border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 font-medium text-left">
                      참여 프로젝트명
                      <div className="text-xs normal-case text-slate-400 mt-1">클릭하여 PPE 상세보기</div>
                    </th>
                    <th className="px-6 py-3 font-medium text-right whitespace-nowrap">빌링액</th>
                    <th className="px-6 py-3 font-medium text-right whitespace-nowrap">투입MM</th>
                  </tr>
                </thead>
                <tbody>
                  {crDetails.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-6 py-8 text-center text-slate-500">
                        참여한 프로젝트가 없습니다.
                      </td>
                    </tr>
                  ) : (
                    crDetails.map((detail, index) => (
                      <tr key={index} className="border-t border-slate-200">
                        <td className="px-6 py-4">
                          <button
                            onClick={() => onProjectClick(detail.projectId, detail.projectName)}
                            className="font-semibold text-indigo-600 hover:text-indigo-800 hover:underline cursor-pointer text-left"
                          >
                            {detail.projectName}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-right whitespace-nowrap">{formatNumber(detail.projectRevenue)}</td>
                        <td className="px-6 py-4 text-right whitespace-nowrap">{formatMM(detail.yearlyMM)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                {crDetails.length > 0 && (
                  <tfoot>
                    {/* Revenue & Utility 합계 */}
                    <tr className="border-t-2 border-slate-300 bg-slate-50">
                      <td className="px-6 py-3 font-semibold text-slate-700">Revenue & Utility 합계</td>
                      <td className="px-6 py-3 text-right font-semibold">{formatNumber(totalBilling)}</td>
                      <td className="px-6 py-3 text-right font-semibold">{formatMM(totalMM)}</td>
                    </tr>
                    {/* 연간 인력원가 */}
                    <tr className="border-t border-slate-200 bg-slate-50">
                      <td className="px-6 py-3 font-semibold text-slate-700">연간 인력원가</td>
                      <td className="px-6 py-3 text-right font-semibold">{formatNumber(annualLaborCost)}</td>
                      <td className="px-6 py-3 text-right font-semibold">12</td>
                    </tr>
                    {/* Covered Revenue */}
                    <tr className="border-t-2 border-slate-400 bg-white">
                      <td className="px-6 py-4 font-bold text-slate-900 text-base">Covered Revenue</td>
                      <td className={`px-6 py-4 text-right font-bold text-base ${coveredRevenuePct < 100 ? 'text-red-600' : 'text-slate-900'}`}>
                        {coveredRevenuePct.toFixed(1)}%
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-base text-slate-900">
                        {mmPct.toFixed(1)}%
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CRManagement: React.FC = () => {
  const { showToast } = useAlert();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [crDetails, setCrDetails] = useState<CRDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  // PPE 모달 관련 상태
  const [isPPEModalOpen, setIsPPEModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<BackendProject | null>(null);

  // 사용 가능한 연도 목록 로드
  useEffect(() => {
    const fetchAvailableYears = async () => {
      try {
        const years = await crApi.getAvailableYears();
        setAvailableYears(years);
        // 가져온 연도 목록에 현재 선택된 연도가 없으면 첫 번째 연도로 변경
        if (years.length > 0 && !years.includes(selectedYear)) {
          setSelectedYear(years[0]);
        }
      } catch (error) {
        showToast('연도 목록을 불러올 수 없습니다.', 'error');
        setAvailableYears([new Date().getFullYear()]);
      }
    };
    fetchAvailableYears();
  }, []);

  useEffect(() => {
    loadEmployeeCRList();
  }, [selectedYear]);

  const loadEmployeeCRList = async () => {
    try {
      setLoading(true);
      const data = await crApi.getEmployeeCRList(selectedYear);
      setEmployees(data);
    } catch (error) {
      showToast('CR 데이터를 불러올 수 없습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openPanel = (employee: Employee) => {
    
    setSelectedEmployee(employee);
    setIsPanelOpen(true);
    
    // calculationDetails가 있으면 바로 파싱해서 표시
    if (employee.calculationDetails) {
      try {
        const parsedDetails = JSON.parse(employee.calculationDetails);
        // 새로운 구조: { projects: [...], summary: {...} }
        const projectsArray = parsedDetails.projects || parsedDetails;
        const formattedDetails = projectsArray.map((detail: any) => {
          return {
            employeeId: employee.id || 0,
            projectId: detail.projectId,
            projectName: detail.projectName,
            projectRevenue: detail.billingAmount || detail.employeeRevenue || detail.revenue || 0,
            inputCost: detail.annualLaborCost || 0,
            cr: detail.projectCR,
            yearlyMM: detail.yearlyMM || 0
          };
        }).sort((a: any, b: any) => b.projectRevenue - a.projectRevenue);
        setCrDetails(formattedDetails);
      } catch (error) {
        setCrDetails([]);
      }
    } else {
      setCrDetails([]);
    }
  };

  const closePanel = () => {
    setSelectedEmployee(null);
    setIsPanelOpen(false);
    setCrDetails([]);
  };

  const handleProjectClick = async (projectId: number, projectName: string) => {
    try {
      // 프로젝트 ID로 프로젝트 정보 조회
      const project = await projectApi.getById(projectId);

      if (project) {
        setSelectedProject(project);
        setIsPPEModalOpen(true);
      } else {
        showToast(`프로젝트 정보를 찾을 수 없습니다. (${projectName})`, 'error');
      }
    } catch (error) {
      // 삭제된 프로젝트일 수 있음
      showToast(`프로젝트 정보를 로딩할 수 없습니다. (${projectName}) 프로젝트가 삭제되었을 수 있습니다.`, 'error');
    }
  };

  const closePPEModal = () => {
    setSelectedProject(null);
    setIsPPEModalOpen(false);
  };

  const handlePPESave = async (updatedData: any) => {
    // PPE 저장 후 모달 닫기 (CR 페이지에서는 업데이트만 가능)
    if (updatedData.type === 'update') {
      closePPEModal();
    }
  };

  return (
    <div className="p-6 lg:p-8">
      <header className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">인사평가</h1>
          <p className="text-slate-500 mt-1">직원별 빌링원가 대비 인건비 효율성을 관리합니다. 직원별 투입 비율에 따른 매출액 기준으로 투입원가 비중과 CR 비율을 계산합니다.</p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {availableYears.filter(year => year >= 2025).map(year => (
              <option key={year} value={year}>{year}년</option>
            ))}
          </select>
        </div>
      </header>
      
      {/* 직원별 CR 목록 */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-800">연간 Covered Revenue</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase bg-slate-50">
              <tr>
                <th className="px-6 py-3 font-medium text-left">사번</th>
                <th className="px-6 py-3 font-medium text-left">이름</th>
                <th className="px-6 py-3 font-medium text-left">직무</th>
                <th className="px-6 py-3 font-medium text-left">직급</th>
                <th className="px-6 py-3 font-medium text-right">CR (%)</th>
              </tr>
            </thead>
            <tbody className="text-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                    CR 데이터를 불러오는 중...
                  </td>
                </tr>
              ) : employees.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                    CR 데이터가 없습니다.
                  </td>
                </tr>
              ) : (
                employees.map((employee) => {
                  const isResigned = employee.status === 'RESIGNED';
                  return (
                    <tr
                      key={employee.id}
                      className={`border-t border-slate-200 hover:bg-slate-50 cursor-pointer ${isResigned ? 'bg-slate-50 opacity-60' : ''}`}
                      onClick={() => openPanel(employee)}
                    >
                      <td className="px-6 py-4">{employee.emp_no}</td>
                      <td className="px-6 py-4 font-semibold text-slate-900">
                        {employee.name}
                        {isResigned && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-400 text-white">
                            퇴사
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">{employee.department}</td>
                      <td className="px-6 py-4">{employee.position}</td>
                      <td className={`px-6 py-4 text-right font-bold ${Number(employee.totalCR || 0) < 100 ? 'text-red-600' : 'text-slate-900'}`}>
                        {Number(employee.totalCR || 0).toFixed(1)}%
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 월별 Utility 테이블 */}
      {!loading && employees.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mt-6">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-bold text-slate-800">월별 Utility</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                <tr>
                  <th className="px-4 py-3 font-medium text-left whitespace-nowrap">직원명</th>
                  {Array.from({ length: 12 }, (_, i) => (
                    <th key={i} className="px-3 py-3 font-medium text-right whitespace-nowrap">{i + 1}월</th>
                  ))}
                  <th className="px-4 py-3 font-medium text-right whitespace-nowrap">총투입MM</th>
                  <th className="px-4 py-3 font-medium text-right whitespace-nowrap">Utility %</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                {employees.map((employee) => {
                  const isResigned = employee.status === 'RESIGNED';
                  const monthlyMM = new Array(12).fill(0);
                  if (employee.calculationDetails) {
                    try {
                      const parsed = JSON.parse(employee.calculationDetails);
                      const projects = parsed.projects || parsed;
                      (projects as any[]).forEach((project: any) => {
                        if (project.monthlyDetails) {
                          project.monthlyDetails.forEach((md: any) => {
                            if (md.month >= 1 && md.month <= 12) {
                              monthlyMM[md.month - 1] += md.mm || 0;
                            }
                          });
                        }
                      });
                    } catch {
                      // calculationDetails JSON 파싱 실패 시 무시
                    }
                  }
                  const totalMM = monthlyMM.reduce((sum, v) => sum + v, 0);
                  const utilityPct = (totalMM / 12) * 100;

                  return (
                    <tr
                      key={employee.id}
                      className={`border-t border-slate-200 ${isResigned ? 'bg-slate-50 opacity-60' : ''}`}
                    >
                      <td className="px-4 py-3 font-semibold text-slate-900 whitespace-nowrap">
                        {employee.name}
                        {isResigned && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-400 text-white">
                            퇴사
                          </span>
                        )}
                      </td>
                      {monthlyMM.map((mm, i) => (
                        <td
                          key={i}
                          className={`px-3 py-3 text-right whitespace-nowrap ${mm > 1.2 ? 'text-red-600 font-bold' : ''}`}
                        >
                          {mm > 0 ? mm.toFixed(1) : '-'}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">{totalMM.toFixed(1)}</td>
                      <td className="px-4 py-3 text-right font-bold whitespace-nowrap">{utilityPct.toFixed(1)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <CRDetailPanel
        isOpen={isPanelOpen}
        employee={selectedEmployee}
        crDetails={crDetails}
        selectedYear={selectedYear}
        onClose={closePanel}
        onProjectClick={handleProjectClick}
      />

      <NewProjectModal
        isOpen={isPPEModalOpen}
        onClose={closePPEModal}
        project={selectedProject || undefined}
        onSave={handlePPESave}
        readOnly={true}
      />
    </div>
  );
};

export default CRManagement;